import {encodeBase64BigEndian, fillZeroL} from "../util/binary";
import {CMP_ID, CMP_SS_COOKIE, CMP_VERSION} from "../cmp/cmp";
import cmpStatus from "../cmp-status";
import cmpConfig from "../cmp-config";
import {FieldDefTCStringDecoder} from "./decoder/field-def-tc-string-decoder";
import {site} from "../context/context";
import cookies from "services-comun/modules/browser/cookies";

interface ISpecialFeatureOptIns {
    [key:number]:boolean;
}
interface IPurposesConsent {
    [key:number]:boolean;
}
interface IPurposesLITransparency {
    [key:number]:boolean;
}
interface ISpecificJurisdictionDisclosures {
    purposeOneTreatment:boolean;
    publisherCC:string;
}

interface IBitField {
    [key:number]:boolean;
}

interface IBitFieldSection {
    bitField:IBitField;
}

interface IRangeEntry {
    isARange:boolean;
    startOrOnlyVendorId:number;
    endVendorId?:number;
}

interface IRangeSection {
    numEntries:number;
    rangeEntries:IRangeEntry[];
}

interface IVendorConsentSection {
    maxVendorId:number;
    isRangeEnconding:boolean;
    bitFieldSection?:IBitFieldSection;
    rangeSection?:IRangeSection;
}

interface IVendorLegitimateInterestSection {
    maxVendorId:number;
    isRangeEnconding:boolean;
    bitFieldSection?:IBitFieldSection;
    rangeSection?:IRangeSection;
}

interface IPubRestrictionEntry {
    purposeId:number;
    restrictionType:number;
    numEntries:number;
    rangeEntries:IRangeEntry[];
}

interface IPublisherRestrictionsSection {
    numPubRestrictions:number;
    pubRestrictionEntries?:IPubRestrictionEntry[];
}

interface ICoreString {
    version:number;
    created:number;
    lastUpdated:number;
    cmpId:number;
    cmpVersion:number;
    consentScreen:number;
    consentLanguage:string;
    vendorListVersion:number;
    tcfPolicyVersion:number;
    isServiceSpecific:boolean;
    useNonStandardStacks:boolean;
    specialFeatureOptIns:ISpecialFeatureOptIns;
    purposesConsent:IPurposesConsent;
    purposesLITransparency:IPurposesLITransparency;
    specificJurisdictionDisclosures:ISpecificJurisdictionDisclosures;
    vendorConsentSection:IVendorConsentSection;
    vendorLegitimateInterestSection:IVendorLegitimateInterestSection;
    publisherRestrictionsSection:IPublisherRestrictionsSection;
}

interface ITCStringConfig {
    specialFeatureOptIns:ISpecialFeatureOptIns;
    purposesConsent:IPurposesConsent;
    purposesLITransparency:IPurposesLITransparency;
    maxVendorId: number;
    vendors: any;
    vendorsLI: any;
    acceptedVendorsIds: number[];
    maxVendorLIId: number;
    acceptedVendorsLIIds: number[];
}

class TCString {
    /* STATIC */

    private static LETTERS:string = 'abcdefghijklmnopqrstuvwxyz';

    public static buildProxy(encodedString?:string) : TCString {
        const tcString: TCString = new TCString();

        if (encodedString) {
            // Decodificamos los datos de la cookie
            new FieldDefTCStringDecoder().decode(encodedString.split('.')[0], tcString.core);

            // Completamos información adicional
            if (tcString.core.vendorConsentSection.isRangeEnconding) {
                // Completamos bitmap
                const bitField:IBitField = {};

                for (let vendorId:number = 1; vendorId <= tcString.core.vendorConsentSection.maxVendorId; vendorId++) {
                    const ranges:IRangeEntry[] = tcString.core.vendorConsentSection.rangeSection?.rangeEntries.filter((rangeEntry:IRangeEntry) => {
                        return (!rangeEntry.isARange && rangeEntry.startOrOnlyVendorId == vendorId)
                            || (rangeEntry.isARange && rangeEntry.startOrOnlyVendorId <= vendorId && rangeEntry.endVendorId||0 >= vendorId);
                    })??[];
                    bitField[vendorId] = ranges.length == 1;
                }

                tcString.core.vendorConsentSection.bitFieldSection = {
                    bitField: bitField
                };
            } else {
                // Completamos range
                if (tcString.core.vendorConsentSection.bitFieldSection) {
                    const rangeEntries:IRangeEntry[] = TCString.buildRanges(Object.keys(tcString.core.vendorConsentSection.bitFieldSection.bitField)
                        .map((vendorIdStr:string) => {return parseInt(vendorIdStr);})
                        .filter((vendorId:number) => {return (tcString.core.vendorConsentSection.bitFieldSection as IBitFieldSection).bitField[vendorId];}));
                    tcString.core.vendorConsentSection.rangeSection = {
                        numEntries: rangeEntries.length,
                        rangeEntries: rangeEntries
                    };
                }
            }

            if (tcString.core.vendorLegitimateInterestSection.isRangeEnconding) {
                // Completamos bitmap
                const bitField:IBitField = {};

                for (let vendorId:number = 1; vendorId <= tcString.core.vendorLegitimateInterestSection.maxVendorId; vendorId++) {
                    const ranges:IRangeEntry[] = tcString.core.vendorLegitimateInterestSection.rangeSection?.rangeEntries.filter((rangeEntry:IRangeEntry) => {
                        return (!rangeEntry.isARange && rangeEntry.startOrOnlyVendorId == vendorId)
                            || (rangeEntry.isARange && rangeEntry.startOrOnlyVendorId <= vendorId && rangeEntry.endVendorId||0 >= vendorId);
                    })??[]
                    bitField[vendorId] = ranges.length == 1;
                }

                tcString.core.vendorLegitimateInterestSection.bitFieldSection = {
                    bitField: bitField
                };
            } else {
                // Completamos range
                if (tcString.core.vendorLegitimateInterestSection.bitFieldSection) {
                    const rangeEntries:IRangeEntry[] = TCString.buildRanges(Object.keys(tcString.core.vendorLegitimateInterestSection.bitFieldSection.bitField)
                        .map((vendorIdStr:string) => {return parseInt(vendorIdStr);})
                        .filter((vendorId:number) => {return (tcString.core.vendorLegitimateInterestSection.bitFieldSection as IBitFieldSection).bitField[vendorId];}));
                    tcString.core.vendorLegitimateInterestSection.rangeSection = {
                        numEntries: rangeEntries.length,
                        rangeEntries: rangeEntries
                    };
                }

            }

        }

        return tcString;
    }

    /**
     * Construye la sección BitFieldSection para VendorConsentSection.
     */
    private static buildVendorConsentSectionBitFieldSection(maxVendorId:number, vendors: any) : IBitFieldSection {
        const bitField:IBitField = {};

        for (let vendorId:number = 1; vendorId <= maxVendorId; vendorId++) {
            bitField[vendorId] = vendors[vendorId];
        }

        return {
            bitField: bitField
        };
    }

    /**
     * Construye la sección BitFieldSection para VendorConsentSection.
     */
    private static buildVendorLISectionBitFieldSection(maxVendorId:number, vendorsLI: any) : IBitFieldSection {
        const bitField:IBitField = {};

        for (let vendorId:number = 1; vendorId <= maxVendorId; vendorId++) {
            bitField[vendorId] = vendorsLI[vendorId];
        }

        return {
            bitField: bitField
        };
    }

    /**
     * Construye la sección RangeSection para VendorConsentSection.
     */
    private static buildVendorConsentSectionRangeSection(acceptedVendorsIds: number[]) : IRangeSection {
        const rangeEntries:IRangeEntry[] = TCString.buildRanges(acceptedVendorsIds);
        return {
            numEntries: rangeEntries.length,
            rangeEntries: rangeEntries
        };
    }

    /**
     * Construye la sección RangeSection para VendorConsentSection.
     */
    private static buildVendorLISectionRangeSection(acceptedVendorsLIIds: number[]) : IRangeSection {
        const rangeEntries:IRangeEntry[] = TCString.buildRanges(acceptedVendorsLIIds);
        return {
            numEntries: rangeEntries.length,
            rangeEntries: rangeEntries
        };
    }

    /**
     * Construye los rangos para
     */
    private static buildRanges(vendorIds:number[] = []) : IRangeEntry[] {
        const entries:IRangeEntry[] = [];

        // Obtenemos los vendors aceptados
        const vendorsIds:number[] = vendorIds.sort((vendorIdA:number, vendorsIdB:number) => {
            if (vendorIdA < vendorsIdB) {return -1;}
            if (vendorIdA > vendorsIdB) {return 1;}
            return 0;
        });

        if (vendorsIds.length == 0) {return [];}

        let startVendorId:number = vendorsIds.shift()??0;
        let lastVendorId:number = startVendorId;
        while (vendorsIds.length) {
            const currentVendorId:number = vendorsIds.shift()??0;
            if (currentVendorId == lastVendorId + 1) {
                lastVendorId = currentVendorId;
            } else {
                entries.push(TCString.createRangeEntry(startVendorId, lastVendorId));
                startVendorId = currentVendorId;
                lastVendorId = currentVendorId;
            }
        }
        entries.push(TCString.createRangeEntry(startVendorId, lastVendorId));

        return entries;
    }

    /* INSTANCE */

    private readonly core: ICoreString;

    private constructor() {
        const consentLanguage:string = site.language;
        this.core = {
            version: 2,
            created: Math.round((new Date()).setUTCHours(0,0,0,0)/100),
            lastUpdated: Math.round((new Date()).setUTCHours(0,0,0,0)/100),
            cmpId: CMP_ID,
            cmpVersion: CMP_VERSION,
            consentScreen: 1,
            consentLanguage: consentLanguage.toLowerCase(),
            vendorListVersion: cmpStatus.gvlVersion??0,
            tcfPolicyVersion: cmpStatus.tfcPolicyVersion??0,
            isServiceSpecific: cmpConfig.serviceSpecific,
            useNonStandardStacks: false,
            specialFeatureOptIns: {},
            purposesConsent: {},
            purposesLITransparency: {},
            specificJurisdictionDisclosures: {
                purposeOneTreatment: false,
                publisherCC: site.country.toLowerCase()
            },
            vendorConsentSection: {
                maxVendorId: 0,
                isRangeEnconding: false,
                bitFieldSection: {
                    bitField: {}
                },
                rangeSection: {
                    numEntries: 0,
                    rangeEntries: []
                }
            },
            vendorLegitimateInterestSection: {
                maxVendorId: 0,
                isRangeEnconding: false,
                bitFieldSection: {
                    bitField: []
                },
                rangeSection: {
                    numEntries: 0,
                    rangeEntries: []
                }
            },
            publisherRestrictionsSection: {
                numPubRestrictions: 0
            }
        };
    }

    public cmpVersion() : number {
        return this.core.cmpVersion;
    }

    public acceptedPurposes() : number[] {
        return Object.keys(this.core.purposesConsent)
            .filter((key:string) => {return this.core.purposesConsent[parseInt(key)] === true;})
            .map((key: string) => {return parseInt(key);})
        ;
    }

    public acceptedPurposesLI() : number[] {
        return Object.keys(this.core.purposesLITransparency)
            .filter((key:string) => {return this.core.purposesLITransparency[parseInt(key)] === true;})
            .map((key: string) => {return parseInt(key);})
        ;
    }

    public acceptedSpecialFeatures() : number[] {
        return Object.keys(this.core.specialFeatureOptIns)
            .filter((key:string) => {return this.core.specialFeatureOptIns[parseInt(key)] === true;})
            .map((key: string) => {return parseInt(key);})
        ;
    }

    public update(config:ITCStringConfig) : void {
        this.core.lastUpdated = Math.round((new Date()).setUTCHours(0,0,0,0)/100);
        this.core.specialFeatureOptIns = config.specialFeatureOptIns;
        this.core.purposesConsent = config.purposesConsent;
        this.core.purposesLITransparency = config.purposesLITransparency;
        this.core.vendorConsentSection.maxVendorId = config.maxVendorId;
        this.core.vendorConsentSection.bitFieldSection = TCString.buildVendorConsentSectionBitFieldSection(config.maxVendorId, config.vendors);
        this.core.vendorConsentSection.rangeSection = TCString.buildVendorConsentSectionRangeSection(config.acceptedVendorsIds);
        this.core.vendorLegitimateInterestSection.maxVendorId = config.maxVendorLIId;
        this.core.vendorLegitimateInterestSection.bitFieldSection = TCString.buildVendorLISectionBitFieldSection(config.maxVendorLIId, config.vendorsLI);
        this.core.vendorLegitimateInterestSection.rangeSection = TCString.buildVendorLISectionRangeSection(config.acceptedVendorsLIIds)
    }

    /**
     * Construye la cadena binaria de TcString.
     */
    public toString() : string {
        const core:string = this.coreToString();
        return `${encodeBase64BigEndian(core)}`;
    }

    /**
     * Construye la cadena binaria de CoreString.
     */
    private coreToString() : string {
        const core:ICoreString = this.core;
        const version:string = fillZeroL(core.version.toString(2), 6);
        const created:string = fillZeroL(core.created.toString(2), 36);
        const lastUpdated:string = fillZeroL(core.lastUpdated.toString(2), 36);
        const cmpId:string = fillZeroL(core.cmpId.toString(2), 12);
        const cmpVersion:string = fillZeroL(core.cmpVersion.toString(2), 12);
        const consentScreen:string = fillZeroL(core.consentScreen.toString(2), 6);
        const consentLanguage:string = `${fillZeroL(`${TCString.LETTERS.indexOf(core.consentLanguage.substr(0, 1)).toString(2)}`, 6)}${fillZeroL(`${TCString.LETTERS.indexOf(core.consentLanguage.substr(1, 1)).toString(2)}`, 6)}`;
        const vendorListVersion:string = fillZeroL(core.vendorListVersion.toString(2), 12);
        const tcfPolicyVersion:string = fillZeroL(core.tcfPolicyVersion.toString(2), 6);
        const isServiceSpecific:string = core.isServiceSpecific ? '1' : '0';
        const useNonStandardStacks:string = core.useNonStandardStacks ? '1' : '0';

        let specialFeatureOptIns:string = '';
        for (let idx:number = 1; idx <= 12; idx++) {
            const bitValue:string = core.specialFeatureOptIns[idx] ? '1' : '0';
            specialFeatureOptIns += bitValue;
        }
        specialFeatureOptIns = fillZeroL(specialFeatureOptIns, 12);

        let purposesConsents:string = '';
        for (let idx:number = 1; idx <= 24; idx++) {
            const bitValue:string = core.purposesConsent[idx] ? '1' : '0';
            purposesConsents += bitValue;
        }
        purposesConsents = fillZeroL(purposesConsents, 24);

        let purposesLITransparency:string = '';
        for (let idx:number = 1; idx <= 24; idx++) {
            const bitValue:string = core.purposesLITransparency[idx] ? '1' : '0';
            purposesLITransparency += bitValue;
        }
        purposesLITransparency = fillZeroL(purposesLITransparency, 24);

        const purposeOneTreatment:string = core.specificJurisdictionDisclosures.purposeOneTreatment ? '1' : '0';
        const publisherCC:string = `${fillZeroL(`${TCString.LETTERS.indexOf(core.specificJurisdictionDisclosures.publisherCC.substr(0, 1)).toString(2)}`, 6)}${fillZeroL(`${TCString.LETTERS.indexOf(core.specificJurisdictionDisclosures.publisherCC.substr(1, 1)).toString(2)}`, 6)}`;

        return `${version}${created}${lastUpdated}${cmpId}${cmpVersion}${consentScreen}${consentLanguage}${vendorListVersion}${tcfPolicyVersion}${isServiceSpecific}${useNonStandardStacks}${specialFeatureOptIns}${purposesConsents}${purposesLITransparency}${purposeOneTreatment}${publisherCC}${this.coreVendorConsentSectionToString()}${this.coreVendorLISectionToString()}${this.corePublisherRestrictionsSectionToString()}`;
    }

    /**
     * Construye la cadena binaria de la sección Core - VendorConsentSection.
     */
    private coreVendorConsentSectionToString() : string {
        return this.coreVendorGenericSectionToString(this.core.vendorConsentSection);
    }

    /**
     * Construye la cadena binaria de la sección Core - VendorLegitimateInterestSection.
     */
    private coreVendorLISectionToString() : string {
        return this.coreVendorGenericSectionToString(this.core.vendorLegitimateInterestSection);
    }

    /**
     * Construye la cadena binaria de la sección de vendors (Genérico).
     */
    private coreVendorGenericSectionToString(section: any) : string {
        const maxVendorId:string = fillZeroL((section.maxVendorId || 0).toString(2), 16);

        // Calculamos el tamaño de cada representación
        const rangeBits:number = this.calculateRangeSectionBits(section.rangeSection);
        const bitFieldBits:number = section.maxVendorId;
        const isRangeEncoding:string = rangeBits < bitFieldBits ? '1' : '0';

        let rangeOrBitFieldBits:string = '';
        if (isRangeEncoding == '1') {
            rangeOrBitFieldBits += fillZeroL(section.rangeSection.numEntries.toString(2), 12);
            section.rangeSection.rangeEntries.forEach((rangeEntry:IRangeEntry) => {
                rangeOrBitFieldBits += rangeEntry.isARange ? '1' : '0';
                rangeOrBitFieldBits += fillZeroL(rangeEntry.startOrOnlyVendorId.toString(2), 16);
                if (rangeEntry.isARange) {
                    rangeOrBitFieldBits += fillZeroL((rangeEntry.endVendorId as number).toString(2), 16);
                }
            });
        } else {
            Object.values(section.bitFieldSection.bitField).forEach(selected => {
                rangeOrBitFieldBits += selected ? '1' : '0';
            });
        }
        return `${maxVendorId}${isRangeEncoding}${rangeOrBitFieldBits}`;
    }

    /**
     * Construye la cadena binaria de la sección Core - PublisherRestrictionsSection.
     */
    private corePublisherRestrictionsSectionToString() : string {
        const core:ICoreString = this.core;
        const numPubRestrictions:string = fillZeroL(core.publisherRestrictionsSection.numPubRestrictions.toString(2), 12);
        return `${numPubRestrictions}`;
    }

    private calculateRangeSectionBits(rangeSection:IRangeSection) : number {
        // Iniciamos el número de bits en 12, que son los necesarios para numEntries.
        let bits:number = 12;
        if (rangeSection.rangeEntries) {
            rangeSection.rangeEntries.forEach((rangeEntry:IRangeEntry) => {
                bits++; // +1 bit por isARange.
                if (rangeEntry.isARange) {
                    bits += 32;
                } else {
                    bits += 16;
                }
            });
        }
        return bits;
    }

    /**
     * Dados dos Ids de vendors crea un RangeEntry.
     * @param startVendorId Id vendor de inicio
     * @param endVendorId Id de vendor final (puede ser el mismo que startVendorId).
     */
    private static createRangeEntry(startVendorId:number, endVendorId:number) : IRangeEntry {
        const rangeEntry:IRangeEntry = {
            isARange: startVendorId != endVendorId,
            startOrOnlyVendorId: startVendorId
        };
        if (rangeEntry.isARange) {
            rangeEntry.endVendorId = endVendorId;
        }
        return rangeEntry;
    }

    public coreString() : ICoreString {
        return this.core;
    }

    /**
     * Devuelve la configuración de propósitos guardada en la cadenda.
     */
    public get purposes() : IPurposesConsent {
        return this.core.purposesConsent;
    }

    /**
     * Devuelve la configuración de propósitos de LI guardada en la cadenda.
     */
    public get purposesLI() : IPurposesLITransparency {
        return this.core.purposesLITransparency;
    }

    /**
     * Devuelve la configuración de características especiales guardada en la cadenda.
     */
    public get specialFeatures() : ISpecialFeatureOptIns {
        return this.core.specialFeatureOptIns;
    }

    /**
     * Devuelve la configuración de vendors guardada en la cadenda.
     */
    public get vendors() : any {
        return this.vendorGeneric(this.core.vendorConsentSection);
    }

    /**
     * Devuelve la configuración de vendors de LI guardada en la cadenda.
     */
    public get vendorsLI() : any {
        return this.vendorGeneric(this.core.vendorLegitimateInterestSection);
    }

    /**
     * Devuelve la configuración de vendors genéricos guardada en la cadenda.
     */
    private vendorGeneric(section: any) : any {

        let vendorConsent:any = {};

        if (section.isRangeEnconding) {
            section.rangeSection.rangeEntries.forEach((rangeEntry:IRangeEntry) => {
                const start:number = rangeEntry.startOrOnlyVendorId;
                let end:number = start;
                if (rangeEntry.isARange) {
                    end = rangeEntry.endVendorId as number;
                }
                for (let ind:number = start; ind <= end; ind++) {
                    vendorConsent[ind] = true;
                }
            });
        } else {
            vendorConsent = section.bitFieldSection.bitField;
        }

        return vendorConsent;
    }

    /**
     * Almacena la cadena en una cookie.
     * @param expirationDays Número de días de expiración de la cookie.
     */
    public store(expirationDays: number): TCString {
        const expiration:number = Date.now() + expirationDays * 24 * 60 * 60 * 1000;
        cookies.set(CMP_SS_COOKIE, this.toString(), new Date(expiration));
        return this;
    }

    /**
     * Versión del CMP con la que se creó la TCString.
     */
    public get version(): number {
        return this.core.version;
    }

    /**
     * Fecha de creación de la TCString.
     */
    public get created(): Date {
        return new Date(this.core.created * 100);
    }
}

export {TCString}
